import * as Sentry from '@sentry/react'
import { AxiosProgressEvent } from 'axios'

import { request } from '@/services/request/request.api'

import {
	IShotlist,
	IStopTranscribingStatus,
	IUploadSrtStatus,
	IVideoUploadJob,
	IVideoUploadJobStatus
} from '@/types/shotlist.interface'
import { ITask } from '@/types/task.interface'

const UPLOAD_SPEED_THRESHOLD_MBPS = 5

const getUploadSpeedMbps = (fileSizeBytes: number, durationSec: number) => {
	const fileSizeMB = fileSizeBytes / (1024 * 1024)

	return (fileSizeMB * 8) / durationSec
}

const captureSlowUpload = (
	taskId: string,
	fileSizeBytes: number,
	durationSec: number,
	speedMbps: number
) => {
	const uploadUrl = `/shotlists/${taskId}/video`

	Sentry.captureMessage('Slow file upload', {
		level: 'warning',
		tags: {
			upload_url: uploadUrl,
			threshold_mbps: UPLOAD_SPEED_THRESHOLD_MBPS
		},
		contexts: {
			upload_speed: {
				speed_mbps: speedMbps,
				file_size_mb: fileSizeBytes / (1024 * 1024),
				duration_sec: durationSec,
				task_id: taskId
			}
		}
	})
}

export const ShotlistsService = {
	async createShotlistTask(fname: string) {
		const formData = new URLSearchParams()
		formData.append('fname', fname)

		return request<{ id: string }>({
			url: '/shotlists',
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded'
			},
			data: formData
		})
	},
	async uploadShotlistVideo(
		taskId: string,
		file: File,
		onUploadProgress?: (event: AxiosProgressEvent) => void,
		signal?: AbortSignal
	) {
		const formData = new FormData()
		formData.append('file', file)

		const uploadStartTime = performance.now()
		let uploadEndTime: number | undefined

		const handleUploadProgress = (event: AxiosProgressEvent) => {
			onUploadProgress?.(event)

			if (event.total && event.loaded >= event.total) {
				uploadEndTime = performance.now()
			}
		}

		const response = await request<IVideoUploadJob>({
			url: `/shotlists/${taskId}/video`,
			method: 'POST',
			data: formData,
			onUploadProgress: handleUploadProgress,
			signal
		})

		const durationSec =
			((uploadEndTime ?? performance.now()) - uploadStartTime) / 1000
		const speedMbps = getUploadSpeedMbps(file.size, durationSec)

		if (speedMbps < UPLOAD_SPEED_THRESHOLD_MBPS) {
			captureSlowUpload(taskId, file.size, durationSec, speedMbps)
		}

		return response
	},
	async getVideoJobStatus(taskId: string, jobId: string) {
		return request<IVideoUploadJobStatus>({
			url: `/shotlists/${taskId}/video/${jobId}/status`,
			method: 'GET'
		})
	},
	async pollVideoJobUntilReady(
		taskId: string,
		jobId: string,
		interval = 3000,
		statusCheckRetries = 3
	) {
		const wait = (ms: number) => new Promise(r => setTimeout(r, ms))

		const getStatusWithRetry = async (): Promise<IVideoUploadJobStatus> => {
			let lastError: unknown

			for (let attempt = 1; attempt <= statusCheckRetries; attempt++) {
				try {
					return await this.getVideoJobStatus(taskId, jobId)
				} catch (error) {
					lastError = error

					if (attempt < statusCheckRetries) {
						await wait(interval)
					}
				}
			}

			throw lastError
		}

		const poll = async (): Promise<IVideoUploadJobStatus> => {
			const status = await getStatusWithRetry()

			if (status.status === 'SUCCESS' && status.success) {
				return status
			}

			if (status.status === 'FAILURE') {
				throw new Error(status.error || 'HLS generation failed')
			}

			await wait(interval)
			return poll()
		}

		return poll()
	},
	async updateShotlistLanguage(taskId: string, language: string) {
		const formData = new URLSearchParams()
		formData.append('language', language)

		return request<IShotlist>({
			url: `/shotlists/${taskId}/update`,
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded'
			},
			data: formData
		})
	},
	async transcribeShotlist(taskId: string) {
		return request<IShotlist>({
			url: `shotlists/${taskId}/transcribe`,
			method: 'POST'
		})
	},
	async getShotlist(id: string) {
		return request<IShotlist>({
			url: `/shotlists/${id}`,
			method: 'GET'
		})
	},
	async translateShotlist(
		id: string,
		language: string,
		source_language: string
	) {
		const formData = new FormData()
		formData.append('language', language)
		formData.append('source_language', source_language)

		return request<IShotlist>({
			url: `/shotlists/${id}/translate`,
			method: 'POST',
			data: formData
		})
	},

	async updateShotlist(taskId: string, content: string, shotlistId: number) {
		const formData = new FormData()
		formData.append('content', content)

		return request<IShotlist>({
			url: `/shotlists/${taskId}/shotlist/${shotlistId}/update`,
			method: 'POST',
			data: formData
		})
	},
	async deleteShotlistTranslation(taskId: string, shotlistId: number) {
		return request<IShotlist>({
			url: `/shotlists/${taskId}/shotlist/${shotlistId}/delete`,
			method: 'POST'
		})
	},
	async uploadSrtFile(
		taskId: string,
		file: File,
		language: string,
		filename?: string
	) {
		const formData = new FormData()
		formData.append('file', file)
		formData.append('language', language)
		formData.append('filename', filename)

		return request<IUploadSrtStatus>({
			url: `/shotlists/${taskId}/srt/upload`,
			method: 'POST',
			data: formData
		})
	},
	async changeOriginalLanguage(taskId: string, language: string) {
		const formData = new FormData()
		formData.append('language', language)

		return request<ITask>({
			url: `/tasks/${taskId}/original_language/change`,
			method: 'POST',
			data: formData
		})
	},
	async stopTranscribing(taskId: string) {
		return request<IStopTranscribingStatus>({
			url: `/tasks/${taskId}/stop`,
			method: 'POST'
		})
	}
}
